import { Box } from "@mui/material";
import Axios from "axios";
import { useSnackbar } from "notistack";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUserContext, useWebSocketContext } from "../contexts";
import useThrottle from "../utilities/throttle";
import { GAME_OVER_TEXT } from "./constants";
import { DrawModal, RematchModal, WinnerModal } from "./GameModals";

import { useMultiplayerContext } from "../contexts/MultiplayerContext";
import Navbar from "./Nav";
import ProgressSnackbarContent from "./ProgressSnackbarContent";
import SearchModal from "./SearchModal";
import TicTacToe from "./TicTacToe";

const GUESS_NO_TURN = "NT";
const GUESS_CORRECT = "CT";
const GUESS_WRONG = "WT";
const GUESS_IDLE = "GT";

const GameController = () => {
  const socket = useWebSocketContext();
  const navigate = useNavigate();
  const searchInputRef = useRef(null);
  const { username, userId } = useUserContext();
  // get the gameID from the URL
  const gameIdURL = new URLSearchParams(window.location.search).get("gameId");
  const sportIdURL = new URLSearchParams(window.location.search).get("sportId");
  const [gameId, setGameId] = useState(gameIdURL);

  const [opponent, setOpponent] = useState(null);
  const [opponentHere, setOpponentHere] = useState(true);
  const [order, setOrder] = useState(null);
  const [serverTimer, setServerTimer] = useState(24);
  const [isGuessTurn, setIsGuessTurn] = useState(GUESS_NO_TURN);
  const isGuessTurnRef = useRef(isGuessTurn);

  const [counterGuess, setCounterGuess] = useState(0);
  const [messageGuess, setMessageGuess] = useState(null);

  const gameIdRef = useRef(gameId);
  const opponentRef = useRef(null);
  const leaveGameTimerRef = React.useRef(null);

  const [headers, setHeaders] = useState({
    cols: [],
    rows: [],
  });
  const [gameState, setGameState] = useState({
    board: Array(9).fill(null),
    nextPlayerOrder: 0,
    winner: null,
    correctGuesses: {
      [username]: 0,
    },
  });
  const isMyTurn = order === gameState?.nextPlayerOrder;
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [usedPlayers, setUsedPlayers] = useState([]);
  const [selectedBoxIndex, setSelectedBoxIndex] = useState(null);
  const [rematchRequested, setRematchRequested] = useState(false); // Opponent sent rematch request
  const [rematchPending, setRematchPending] = useState(false); // Waiting for opponent response
  const { setIsTimerRunning, setCurrentTimer, currentTimer } =
    useMultiplayerContext();

  const [winner, setWinner] = useState(null);
  const [wasForfeit, setWasForfeit] = useState(false);

  const [isDrawModalOpen, setIsDrawModalOpen] = useState(false);
  const [isRematchModalOpen, setIsRematchModalOpen] = useState(false);

  const [isWinnerModalOpen, setIsWinnerModalOpen] = useState(false);

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  useEffect(() => {
    return () => {
      if (leaveGameTimerRef.current !== null) {
        clearTimeout(leaveGameTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (username !== null && socket !== null && gameId !== null) {
      initializeWebsocket(true);
    } else if (username !== null && socket !== null) {
      initializeWebsocket(false);
    }
  }, [username, socket]);

  useEffect(() => {
    setWinner(gameState.winner);
    if (gameState?.winner != null) {
      setIsWinnerModalOpen(true);
      setRematchRequested(false);
      setRematchPending(false);
    }
  }, [gameState?.winner]);

  // Update the ref whenever isGuessTurn changes
  useEffect(() => {
    isGuessTurnRef.current = isGuessTurn;
  }, [isGuessTurn]);

  const initializeWebsocket = (joining = false) => {
    socket.onmessage = (event) => {
      const data = JSON.parse(event.data);

      // Debug logging for all WebSocket messages
      console.log('[WS] Received message:', data.type, data);

      if (data.type === "gameCreated") {
        setGameId(data.gameId);
        gameIdRef.current = data.gameId;
        navigate(`/play/game?gameId=${data.gameId}`);
      } else if (data.type === "gameNotFound") {
        if (!isWinnerModalOpen) {
          setRematchRequested(false);
          setRematchPending(false);
          // displayProgressSnackbar("Game not found...", () => {
          navigate("/play");
          // });
        }
      } else if (data.type === "rejoinGame") {
        console.log('🎮 Rejoining game - Order:', data.order, 'My userId:', userId, 'Opponent:', data.opponent);
        setOpponentHere(true);
        setIsWinnerModalOpen(false);
        setIsRematchModalOpen(false);
        setWasForfeit(false); // Reset forfeit state for new game
        setGameState(data.gameState);
        // Transform API response to match expected structure
        const transformedHeaders = {
          cols: data.gridHeaders.header_columns || data.gridHeaders.cols || [],
          rows: data.gridHeaders.header_rows || data.gridHeaders.rows || []
        };
        setHeaders(transformedHeaders);
        gameIdRef.current = data.gameId;
        setOpponent(data.opponent);
        opponentRef.current = data.opponent;
        setOrder(data.order);
        console.log('✅ Player order set:', data.order, '(0=❌, 1=⭕️)');
        setIsTimerRunning(true);
        setCurrentTimer(data?.currentTimer);
        setServerTimer(data?.currentTimer);
        // Reset search state for new game
        setSelectedBoxIndex(null);
        setSearchOpen(false);
      } else if (data.type === "guessError") {
        // Wrong guess - close search modal and show error
        setSelectedBoxIndex(null);
        setSearchOpen(false);
        setIsGuessTurn(GUESS_NO_TURN);
        displayProgressSnackbar(data.message || "Incorrect guess");
      } else if (data.type === "gameUpdate" || data.type === "disconnect") {
        setSelectedBoxIndex(null);
        setSearchOpen(false);
        setGameState(data.gameState);
        setCurrentTimer(data?.currentTimer || serverTimer);

        if (data?.type === "disconnect") {
          setOpponentHere(false);
        }

        // Detect forfeit: if there's a winner but neither player has 3 correct guesses
        if (data.gameState?.winner && data.gameState?.correctGuesses) {
          const winnerScore = data.gameState.correctGuesses[data.gameState.winner] || 0;
          const isForfeit = winnerScore < 3;
          if (isForfeit) {
            setWasForfeit(true);
          }
        }

        // Check if counter guess is diff
        const newCounterCorrect = data?.gameState?.correctGuesses[userId];
        if (isGuessTurnRef.current === GUESS_IDLE) {
          if (counterGuess < newCounterCorrect) {
            setCounterGuess(newCounterCorrect);
          }

          setTimeout(() => {
            setMessageGuess(null);
          }, 2000);

          setIsGuessTurn(GUESS_NO_TURN);
        }

        setIsGuessTurn(GUESS_NO_TURN);
      } else if (data.type === "drawRequest") {
        setIsWinnerModalOpen(false);
        setIsDrawModalOpen(true);
      } else if (data.type === "drawDenied") {
        if (gameState.winner !== null) {
          setIsWinnerModalOpen(true);
        }
        displayProgressSnackbar(
          "The other player has declined the draw request."
        );
      } else if (data.type === "drawAccepted") {
        setGameState((prevState) => ({
          ...prevState,
          winner: "draw",
        }));
        setIsDrawModalOpen(false);
        setIsWinnerModalOpen(true);
        setRematchRequested(false);
        setRematchPending(false);
      } else if (data.type === "rematchRequest") {
        // Opponent sent rematch request - show Accept/Decline on WinnerModal
        if (opponentHere) {
          setRematchRequested(true);
          setRematchPending(false);
          setIsWinnerModalOpen(true);
          displayProgressSnackbar(`${opponent?.username || 'Opponent'} wants a rematch!`);
        } else {
          displayProgressSnackbar("Rematch request failed - opponent disconnected");
        }
      } else if (data.type === "rematchDenied") {
        // Opponent declined our rematch request
        setRematchPending(false);
        setRematchRequested(false);
        setIsWinnerModalOpen(true);
        displayProgressSnackbar(
          "The other player has declined the rematch request."
        );
      } else if (data.type === "forfeit") {
        // Opponent forfeited or we forfeited
        setWasForfeit(true);
        if (data.gameState) {
          setGameState(data.gameState);
        }
        if (data.gameState?.winner != null) {
          setIsWinnerModalOpen(true);
        }
      } else if (data.type === "opponentLeft") {
        // Opponent disconnected (per integration guide)
        setOpponentHere(false);
        // Close any open rematch modals since opponent is gone
        setIsRematchModalOpen(false);
        if (data.isGameOver) {
          // Game is over, show winner modal
          setWasForfeit(data.forfeit || false);
          setIsWinnerModalOpen(true);
        } else {
          // Opponent left but game continues (or ends)
          displayProgressSnackbar("Opponent left the game");
        }
      } else if (data.type === "rematchStarted") {
        // Backend creates NEW game with NEW ID for rematch
        console.log('[REMATCH] 🎮 Rematch started! New game:', data.gameId);
        console.log('[REMATCH] Full data:', data);

        setIsRematchModalOpen(false);
        setIsWinnerModalOpen(false);
        setWasForfeit(false);
        setRematchRequested(false);
        setRematchPending(false);
        setGameState(data.gameState);

        // Transform headers
        const transformedHeaders = {
          cols: data.gridHeaders.header_columns || data.gridHeaders.cols || [],
          rows: data.gridHeaders.header_rows || data.gridHeaders.rows || []
        };
        setHeaders(transformedHeaders);

        // Update to NEW game ID
        gameIdRef.current = data.gameId;
        setGameId(data.gameId);
        setOpponent(data.opponent);
        opponentRef.current = data.opponent;
        setOrder(data.order);
        setIsTimerRunning(true);
        setCurrentTimer(data?.currentTimer);
        setServerTimer(data?.currentTimer);
        setSelectedBoxIndex(null);
        setSearchOpen(false);
        setOpponentHere(true);

        // Update URL to new game
        navigate(`/play/game?gameId=${data.gameId}&sportId=${sportIdURL}`, { replace: true });
        displayProgressSnackbar("Rematch started! Good luck!");
      } else if (data.type === "rematchError") {
        // Backend rejected rematch request
        setRematchPending(false);
        setRematchRequested(false);
        setIsWinnerModalOpen(true);
        displayProgressSnackbar(data.error || "Rematch failed");
      } else if (data.type === "rematchTimeout") {
        // Opponent didn't respond in time
        setRematchPending(false);
        setRematchRequested(false);
        setIsWinnerModalOpen(true);
        displayProgressSnackbar(data.message || "Opponent did not respond to rematch request");
      } else if (data.type === "rematchRequestExpired") {
        // Rematch request expired while you were viewing it
        setRematchRequested(false);
        setRematchPending(false);
        if (gameState?.winner != null) {
          setIsWinnerModalOpen(true);
        }
        displayProgressSnackbar("Rematch request expired");
      } else if (data.type === "rematchRequestCancelled") {
        // Opponent cancelled their rematch request
        setRematchRequested(false);
        setRematchPending(false);
        if (gameState?.winner != null) {
          setIsWinnerModalOpen(true);
        }
        const reason = data.reason ? ` (${data.reason})` : '';
        displayProgressSnackbar(`Rematch request cancelled${reason}`);
      }
    };

    if (joining) {
      joinGameById();
    } else {
      startNewGame();
    }
  };

  const startNewGame = () => {
    const message = { type: "startNewGame", username };
    socket.send(JSON.stringify(message));
  };

  const joinGameById = () => {
    const message = { type: "joinMatch", gameId: gameIdRef.current };
    socket.send(JSON.stringify(message));
    setOpponentHere(true);
  };

  const handleMakeMove = (index, player) => {
    let p = player;
    p.guessedBy = userId;
    if (socket && socket.readyState === WebSocket.OPEN) {
      // Waiting for Guess Check
      setIsGuessTurn(GUESS_IDLE);

      socket.send(
        JSON.stringify({
          type: "gameUpdate",
          gameId: gameId,
          index: index,
          userId: userId,
          guess: p,
        })
      );
    }
  };

  const forfeit = () => {
    socket.send(JSON.stringify({ type: "forfeit", gameId }));
    // navigate("/play");
  };

  const leaveMatch = () => {
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ type: "cleanup", gameId }));
    }
    setOpponentHere(true);
    navigate("/play");
  };

  const handleRequestDraw = () => {
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(
        JSON.stringify({
          type: "requestDraw",
          gameId: gameIdRef.current,
        })
      );
      displayProgressSnackbar("Waiting for opponent to accept draw...");
    }
  };
  const handleRequestRematch = () => {
    // Validate conditions before sending rematch request
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      displayProgressSnackbar("Connection lost - cannot request rematch");
      return;
    }

    if (!opponentHere) {
      displayProgressSnackbar("Opponent has left - cannot request rematch");
      return;
    }

    if (!gameIdRef.current) {
      displayProgressSnackbar("Game ID missing - cannot request rematch");
      return;
    }

    // Send rematch request (backend will validate game state)
    socket.send(
      JSON.stringify({
        type: "requestRematch",
        gameId: gameIdRef.current,
        opponent: opponent?.userId,
        userId: userId,
      })
    );

    // Set pending state - button will show "Waiting for opponent..."
    setRematchPending(true);
    displayProgressSnackbar("Rematch request sent to opponent");
  };

  const handleAcceptRematch = () => {
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(
        JSON.stringify({
          type: "acceptRematch",
          gameId: gameIdRef.current,
        })
      );
    }
    setRematchRequested(false);
    setRematchPending(false);
  };

  const handleDenyRematch = () => {
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(
        JSON.stringify({
          type: "denyRematch",
          gameId: gameIdRef.current,
        })
      );
    }
    setRematchRequested(false);
    setRematchPending(false);
  };

  const displaySnackbar = (message, variant = "default") => {
    enqueueSnackbar(message, { variant });
  };

  const displayProgressSnackbar = (text, callback = () => {}) => {
    const key = enqueueSnackbar(text, {
      content: (key, message) => (
        <ProgressSnackbarContent
          text={message}
          duration={4000}
          snackbarKey={key}
          closeSnackbar={closeSnackbar}
        />
      ),
    });

    setTimeout(() => {
      closeSnackbar(key);
    }, 4500);
    leaveGameTimerRef.current = setTimeout(() => {
      callback();
    }, 4500);
  };

  let ac = undefined;
  const debouncedRequest = useThrottle(async () => {
    // cancel any in-flight searches
    ac?.abort();
    const abortController = new AbortController();
    ac = abortController;

    let sr;
    // Check if sport is Movies (sport_id 12) - use TMDB API
    if (sportIdURL === "12") {
      // TMDB movie search
      sr = await Axios.get(
        `https://api.themoviedb.org/3/search/movie?api_key=${process.env.REACT_APP_TMDB_API_KEY}&query=${encodeURIComponent(searchInput)}`,
        {
          signal: ac.signal,
        }
      ).catch(function (e) {
        if (Axios.isCancel(e)) {
          console.error(e.message);
          return;
        } else {
          console.error("TMDB search error:", e);
          return;
        }
      });

      // Transform TMDB response to match player search format
      if (sr && sr.data && sr.data.results) {
        const transformedResults = sr.data.results.map((movie, index) => ({
          refIndex: index,
          item: {
            id: movie.id,
            name: movie.title,
            img_url: movie.poster_path
              ? `https://image.tmdb.org/t/p/w200${movie.poster_path}`
              : null,
            seasons: movie.release_date ? movie.release_date.substring(0, 4) : '' // Show year
          }
        }));
        setSearchResults(transformedResults);
      }
    } else {
      // Regular player search for all other sports
      sr = await Axios.get(
        `${process.env.REACT_APP_SEARCH_URL}/game/fuse/search/players?sportId=${sportIdURL}&name=${searchInput}`,
        {
          signal: ac.signal,
        }
      ).catch(function (e) {
        if (Axios.isCancel(e)) {
          console.error(e.message);
          return;
        } else {
          // handle HTTP error...
          return;
        }
      });
      if (sr) {
        setSearchResults(sr.data);
      }
    }
  });

  const handleInputChange = async (event) => {
    const input = event.target.value;
    setSearchInput(input);
    debouncedRequest();
  };

  const handleBoxClick = (index) => {
    // If the game is over, the box already has a correct answer, or the player has already been used, do nothing
    // if (
    //   isGameOver ||
    //   (!isUnlimitedMode && guessesRemaining <= 0) || // <-- Modified this line
    //   boxElements[index].keys?.length > 0 ||
    //   usedPlayers.includes(boxElements[index])
    // ) {
    //   return;
    // }
    // Otherwise open search
    setSelectedBoxIndex(index);
    setSearchOpen(true);
    setSearchInput("");
    setSearchResults([]);
    // Don't display search results until user starts typing
    // setSearchResults([]);
  };

  const handleResultsClick = (player) => {
    if (!isMyTurn) return;
    const exists = gameState?.usedPlayers.some((obj) => obj?.id === player?.id);
    if (exists) {
      return;
    }
    setSearchOpen(false);
    setUsedPlayers((prevUsedPlayers) => [...prevUsedPlayers, player]);
    handleMakeMove(selectedBoxIndex, player);
    setSelectedBoxIndex(null);
  };

  const winnerModalText = () => {
    let gameText = GAME_OVER_TEXT[sportIdURL];
    if (!gameText) {
      return { header: "", body: "" };
    }

    if (winner === userId) {
      return {
        header: gameText.winner.header,
        body: gameText.winner.body,
      };
    } else if (winner === "draw") {
      return { header: "It's a draw", body: "No winners here..." };
    } else {
      return {
        header: gameText.loser.header,
        body: gameText.loser.body,
      };
    }
  };

  return (
    <>
      <Navbar />
      {gameId && (
        <>
          <SearchModal
            searchOpen={searchOpen}
            setSearchOpen={setSearchOpen}
            searchInput={searchInput}
            handleInputChange={handleInputChange}
            searchInputRef={searchInputRef}
            searchResults={searchResults}
            handleResultsClick={handleResultsClick}
            usedPlayers={gameState?.usedPlayers}
            sportId={sportIdURL}
          />
          {!!messageGuess?.length && (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <h1>{messageGuess}</h1>
            </Box>
          )}
          <TicTacToe
            username={username}
            userId={userId}
            headers={headers}
            gameState={gameState}
            onMakeMove={handleMakeMove}
            opponent={opponent}
            order={order}
            onRequestRematch={handleRequestRematch}
            onRequestDraw={handleRequestDraw}
            handleBoxClick={handleBoxClick}
            selectedBoxIndex={selectedBoxIndex}
            currentTimer={currentTimer}
            sportId={sportIdURL}
            forfeit={forfeit}
          />
        </>
      )}
      <WinnerModal
        isOpen={isWinnerModalOpen}
        winner={winner}
        currentUserId={userId}
        currentUsername={username}
        opponent={opponent}
        score={gameState?.correctGuesses}
        forfeit={wasForfeit}
        eloChanges={gameState?.eloChanges}
        onLeave={leaveMatch}
        onRematch={handleRequestRematch}
        onAcceptRematch={handleAcceptRematch}
        onDenyRematch={handleDenyRematch}
        opponentConnected={opponentHere}
        rematchRequested={rematchRequested}
        rematchPending={rematchPending}
      />
      <RematchModal
        isOpen={isRematchModalOpen}
        opponentUsername={opponent?.username}
        onAccept={() => {
          if (socket && socket.readyState === WebSocket.OPEN) {
            socket.send(
              JSON.stringify({
                type: "acceptRematch",
                gameId: gameIdRef.current,
              })
            );
          }
          setIsRematchModalOpen(false);
        }}
        onDeny={() => {
          if (socket && socket.readyState === WebSocket.OPEN) {
            socket.send(
              JSON.stringify({
                type: "denyRematch",
                gameId: gameIdRef.current,
              })
            );
          }
          setIsRematchModalOpen(false);
          if (gameState?.winner !== null) {
            setIsWinnerModalOpen(true);
          }
        }}
      />
      <DrawModal
        isOpen={isDrawModalOpen}
        onAccept={() => {
          if (socket && socket.readyState === WebSocket.OPEN) {
            socket.send(
              JSON.stringify({
                type: "acceptDraw",
                gameId: gameIdRef.current,
              })
            );
          }
          setIsDrawModalOpen(false);
        }}
        onDeny={() => {
          if (socket && socket.readyState === WebSocket.OPEN) {
            socket.send(
              JSON.stringify({
                type: "denyDraw",
                gameId: gameIdRef.current,
                userId: username,
              })
            );
          }
          setIsDrawModalOpen(false);
          if (gameState.winner !== null) {
            setIsWinnerModalOpen(true);
          }
        }}
      />
    </>
  );
};

export default GameController;
